/**
 * Words for a customer update waiting on a record (SA-1029): the strip
 * chip, the band line, the Intake block's heading and refusals, the toast.
 * One place, as queueText.ts is for the queue. A bare code is never printed.
 */
import { formatConfidence } from '@/components/process/proposalText'
import { INTENT_TEXT } from '@/components/process/processEventText'
import { ApiError } from '@/services/apiClient'
import type { RevisionStep } from '@/types/commerce'
import { timeAgo } from './queueText'
import type { CannotRecord } from './waitingResponses'

export const REVIEW_IN_INTAKE = 'Review in Intake'
export const OPEN_IN_INBOX = 'Open in Inbox'
export const APPLY_AS_REVISION = 'Apply as revision'
export const DISMISS = 'Dismiss'
export const NOTHING_NEW = 'Nothing new in this message — every field it names already has that value.'

/** SA-1042 (spec §D10): B's words. The chip is proposalText.SUGGESTED_BY_AI, never with a number (R2). */
export const ACCEPT = 'Accept'
export const EDIT_BEFORE_APPLYING = 'Edit before applying'
export const EDIT_IN_INTAKE = 'Edit in Intake'

/** SA-1044: the step the changed fields send the process back to: the lowest-numbered of their steps, null when none is used. */
export function revisionLanding(steps: Record<string, RevisionStep>, changed: readonly string[]): RevisionStep | null {
  let best: RevisionStep | null = null
  for (const id of changed) {
    const s = steps[id]
    if (s && (best === null || s.number < best.number)) best = s
  }
  return best
}

/** SA-1044: what a save or an Accept does on a running process: goes back to the landing, or stays. */
export function revisionNotice(
  verb: 'Saving' | 'Accepting', number: number, landing: RevisionStep | null, current: RevisionStep | null,
): string {
  if (landing) {
    return `${verb} records revision ${number} and re-opens the process at ${landing.number} ${landing.title}; values entered at that step and later are kept and marked stale.`
  }
  const where = current ? `stays at ${current.number} ${current.title}` : 'stays at its current step'
  return `${verb} records revision ${number}; no step so far has used these values, so the process ${where} and its open task is kept.`
}

const REFUSALS: Record<string, string> = {
  not_waiting: 'This update was already applied or dismissed.',
  record_closed: 'The record is closed. Reinstate it to apply this update.',
  process_closed: "The record's process is closed.",
  source_unavailable: 'The message is no longer stored, so it cannot be extracted.',
  nothing_to_apply: 'Take at least one field, or dismiss the update.',
  is_response: "This message is the customer's answer to the quote. Record it on Quote versions.",
}

export function updateWaitingWords(n: number): string {
  return n === 1 ? 'Customer update waiting' : `${n} customer updates waiting`
}

export function bandUpdateLine(n: number): string {
  return n === 1 ? 'A customer update is waiting' : `${n} customer updates are waiting`
}

/** The Intake section's closed line, first clause (spec §D23). */
export function summaryClause(n: number): string {
  return n === 1 ? '1 customer update waiting' : `${n} customer updates waiting`
}

export function updateHref(orderId: string): string {
  return `/orders?order=${orderId}&panel=intake`
}

export function toastTitle(orderNumber: string): string {
  return `Customer update on ${orderNumber}`
}

export function channelWord(channel: string): string {
  return channel === 'whatsapp-group' ? 'group message' : 'email'
}

const RESPONSE_INTENTS = new Set(['countered', 'accepted', 'declined'])
function isResponseIntent(intent: string): boolean {
  return RESPONSE_INTENTS.has(intent)
}

export function updateBlockHeading(
  u: { fromName: string | null; channel: string; receivedAt: string; intent: string; confidence: number | null },
  now: Date = new Date(),
): string {
  const who = u.fromName ?? channelWord(u.channel)
  const reads = INTENT_TEXT.get(u.intent)
  const conf = u.confidence !== null ? formatConfidence(u.confidence) : null
  const reading = reads ? `reads as ${reads}${conf ? ` (${conf})` : ''}` : null
  const head = isResponseIntent(u.intent) ? 'Customer response' : 'Customer update'
  return [head, who, timeAgo(u.receivedAt, now), reading].filter(Boolean).join(' · ')
}

export function refusalWords(code: string): string {
  return REFUSALS[code] ?? 'That could not be saved.'
}

export function customerUpdateErrorMessage(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === 'string') {
    if (REFUSALS[err.detail]) return REFUSALS[err.detail]
    return /^[a-z0-9_]+$/.test(err.detail) ? 'That could not be saved.' : err.detail
  }
  // A validation 422 (`recordErrorMessage`'s branch): its first message, never the transport text.
  if (err instanceof ApiError && Array.isArray(err.detail)) {
    const first = err.detail[0] as { msg?: string } | undefined
    if (first?.msg) return first.msg
  }
  return err instanceof Error ? err.message : String(err)
}

/** SA-1034 PR 5 (spec §3.1): the strip chip's tip; the chip's own text is `updateWaitingWords`. */
export const UPDATE_CHIP_TIP = 'A customer message asks for a change · opens the Intake section'

/** SA-1047: a customer reply that answers a sent quote (counter, accepted, declined). */
export function responseWaitingWords(n: number): string {
  return n === 1 ? 'Customer response waiting' : `${n} customer responses waiting`
}

export function bandResponseLine(n: number): string {
  return n === 1 ? 'The customer has answered the quote' : `${n} customer responses are waiting`
}

export function messagesWaitingWords(n: number): string {
  return `${n} customer messages waiting`
}

/** The register chip labels (waitingResponses.registerChip). */
export const UPDATE_WAITING_LABEL = 'Update waiting'
export const RESPONSE_WAITING_LABEL = 'Response waiting'
export const MESSAGES_WAITING_LABEL = 'Messages waiting'
/** A 409 `not_waiting` on the Quote versions card's Dismiss. */
export const RESPONSE_NOT_WAITING = 'This response was already recorded or dismissed.'

export const RECORD_IN_QUOTES = 'Record in Quote versions'
export const REVIEW_IN_QUOTES = 'Review in Quote versions'
export const RESPONSE_CHIP_TIP =
  "The customer's email reads as their answer to the quote. Record it or dismiss it in Quote versions."

export function responseHref(orderId: string): string {
  return `/orders?order=${orderId}&panel=quotes`
}

export function responseToastTitle(number: string): string {
  return `Customer response on ${number}`
}

export function responseBannerText(number: string): string {
  return `A customer response arrived on ${number}`
}

export function recordOnText(number: string): string {
  return `Record on ${number}`
}

/** Why a waiting response cannot be recorded now; `refusalLine` is the card's own `respondRefusalText`. */
export function cannotRecordText(c: CannotRecord, refusalLine: string | null): string {
  switch (c.reason) {
    case 'closed': return 'The record is closed, so this answer can only be dismissed.'
    case 'not_sent': return 'No quote has been sent on this record, so there is nothing to answer yet.'
    case 'before_send': return `This reply came before v${c.version} was sent, so it does not answer v${c.version}.`
    case 'answered': return `v${c.version} already has the customer's answer.`
    case 'refused': return refusalLine ?? ''
  }
}
